import Icon from '@/components/ui/AppIcon';

interface Certification {
  name: string;
  description: string;
  icon: string;
}

interface Statistic {
  value: string;
  label: string;
  icon: string;
}

const TrustSignals = () => {
  const certifications: Certification[] = [
    {
      name: 'AS9100 Certified',
      description: 'Aerospace quality management system certification ensuring consistent excellence',
      icon: 'ShieldCheckIcon',
    },
    {
      name: 'ITAR Compliant',
      description: 'International Traffic in Arms Regulations compliance for defense manufacturing',
      icon: 'LockClosedIcon',
    },
    {
      name: 'NADCAP Accredited',
      description: 'Special process accreditation for critical aerospace manufacturing operations',
      icon: 'CheckBadgeIcon',
    },
    {
      name: 'ISO 9001:2015',
      description: 'International quality management standard certification',
      icon: 'DocumentCheckIcon',
    },
  ];

  const statistics: Statistic[] = [
    {
      value: '15+',
      label: 'Years Aerospace Excellence',
      icon: 'CalendarIcon',
    },
    {
      value: '1,200+',
      label: 'Missions Completed',
      icon: 'RocketLaunchIcon',
    },
    {
      value: '99.97%',
      label: 'Quality Success Rate',
      icon: 'StarIcon',
    },
    {
      value: '98.5%',
      label: 'On-Time Delivery',
      icon: 'ClockIcon',
    },
  ];

  return (
    <section className="relative py-24 bg-gradient-to-b from-background to-primary">
      <div className="mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-aerospace-green/10 rounded-full border border-aerospace-green/30 mb-6">
            <Icon name="ShieldCheckIcon" size={20} className="text-aerospace-green" />
            <span className="font-cta text-sm text-aerospace-green">Certified Excellence</span>
          </div>
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Mission-Critical Trust & Compliance
          </h2>
          <p className="font-body text-lg text-technical-gray">
            Industry-leading certifications and proven track record of aerospace manufacturing excellence.
          </p>
        </div>

        {/* Certifications Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {certifications.map((cert) => (
            <div
              key={cert.name}
              className="group bg-card border border-border rounded-xl p-6 hover:border-aerospace-green transition-all duration-300 hover:shadow-glow"
            >
              <div className="w-16 h-16 bg-aerospace-green/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Icon name={cert.icon as any} size={32} className="text-aerospace-green" />
              </div>
              <h3 className="font-headline text-lg font-bold text-foreground mb-2">
                {cert.name}
              </h3>
              <p className="font-body text-sm text-technical-gray leading-relaxed">
                {cert.description}
              </p>
            </div>
          ))}
        </div>

        {/* Statistics Bar */}
        <div className="bg-gradient-to-r from-accent/10 via-mission-orange/10 to-accent/10 border border-accent/30 rounded-2xl p-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {statistics.map((stat) => (
              <div key={stat.label} className="text-center space-y-3">
                <div className="flex justify-center">
                  <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                    <Icon name={stat.icon as any} size={24} className="text-accent" />
                  </div>
                </div>
                <div>
                  <p className="font-headline text-4xl font-bold text-foreground mb-1">
                    {stat.value}
                  </p>
                  <p className="font-body text-sm text-technical-gray">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Statement */}
        <div className="mt-12 text-center max-w-4xl mx-auto">
          <p className="font-body text-lg text-technical-gray leading-relaxed">
            Trusted by leading aerospace, space, and defense organizations worldwide. Our certifications and track record demonstrate unwavering commitment to quality, security, and mission success.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TrustSignals;